import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Home, ArrowLeft } from "lucide-react";

export default function ErrorPage() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  
  // Primary and complementary colors
  const primaryColor = "#003366";
  const secondaryColor = "#0077CC"; // Lighter blue that complements primary
  
  useEffect(() => {
    setMounted(true);
    
    // Add some particle effects to the background
    const interval = setInterval(() => {
      if (document.getElementById('particles-container')) {
        createParticle();
      }
    }, 400);
    
    return () => {
      setMounted(false);
      clearInterval(interval);
    };
  }, []);

  const handleGoHome = () => {
    setMounted(false);
    setTimeout(() => {
      navigate("/");
    }, 400);
  };
  
  const handleGoBack = () => {
    setMounted(false);
    setTimeout(() => {
      navigate(-1);
    }, 400);
  };
  
  const createParticle = () => {
    const container = document.getElementById('particles-container');
    if (!container) return;
    
    const particle = document.createElement('div');
    particle.className = 'absolute rounded-full';
    
    // Random size between 4-10px
    const size = Math.floor(Math.random() * 6) + 4;
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    
    // Position randomly within the container
    const posX = Math.floor(Math.random() * container.offsetWidth);
    const posY = Math.floor(Math.random() * container.offsetHeight);
    particle.style.left = `${posX}px`;
    particle.style.top = `${posY}px`;
    
    // Use primary or secondary colors
    const colorChoice = Math.random() > 0.7 ? secondaryColor : primaryColor;
    particle.style.backgroundColor = colorChoice;
    particle.style.opacity = (Math.random() * 0.3 + 0.1).toString();
    
    // Animation
    particle.style.animation = `float 8s linear forwards`;
    
    container.appendChild(particle);
    
    // Remove after animation completes
    setTimeout(() => {
      if (particle.parentNode === container) {
        container.removeChild(particle);
      }
    }, 8000);
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-slate-50 overflow-hidden">
      {/* Particles container */}
      <div id="particles-container" className="absolute inset-0 pointer-events-none" />
      
      {/* Main content */}
      <div 
        className={`relative z-10 text-center p-10 rounded-xl max-w-xl 
                   bg-white/90 backdrop-blur-sm shadow-2xl transform transition-all duration-500 
                   ${mounted ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`}
      >
        <div className="relative mb-8">
          {/* Fixed warning icon with proper spacing and sizing */}
          <div className="relative mx-auto w-40 h-40 flex items-center justify-center">
            <div 
              className="absolute inset-0 rounded-full opacity-10"
              style={{ 
                backgroundColor: primaryColor,
                animation: "ripple 2.5s infinite ease-out"
              }}
            />
            <div 
              className="absolute inset-4 rounded-full opacity-20"
              style={{ 
                backgroundColor: primaryColor,
                animation: "ripple 2.5s infinite ease-out 0.5s"
              }}
            />
            <div 
              className="relative z-10 rounded-full bg-blue-700 w-24 h-24 flex items-center justify-center"
              style={{ 
                backgroundColor: primaryColor,
                animation: "pulse 2s infinite ease-in-out"
              }}
            >
              <AlertTriangle 
                size={60} 
                className="text-white" 
                strokeWidth={2}
              />
            </div>
          </div>
        </div>
        
        <h1 
          className="text-8xl font-bold mb-2" 
          style={{ color: primaryColor }}
        >
          404
        </h1>
        
        <div 
          className="h-1 w-24 mx-auto rounded-full mb-6"
          style={{ 
            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
            animation: "widthPulse 3s infinite alternate" 
          }}
        />
        
        <h2 className="text-3xl font-semibold mb-4 text-slate-800">
          Page Not Found
        </h2>
        
        <p className="text-xl text-slate-600 mb-8 max-w-md mx-auto">
          We've searched everywhere, but the page you're looking for seems to have gone on vacation.
        </p>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button
            className="group px-6 py-3 rounded-lg text-white font-medium shadow-lg flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300"
            style={{ 
              backgroundColor: primaryColor,
              boxShadow: isHovering ? `0 8px 16px rgba(0, 51, 102, 0.3)` : `0 4px 6px rgba(0, 51, 102, 0.2)`
            }}
            onClick={handleGoHome}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <Home size={18} />
            <span>Return Home</span>
            <span 
              className="absolute bottom-0 left-0 h-1 rounded-b-lg transition-all duration-300 opacity-0 group-hover:opacity-100"
              style={{ 
                backgroundColor: secondaryColor,
                width: "0%",
                animation: "group-hover:growWidth 0.3s forwards"
              }}
            />
          </button>
          
          <button
            className="px-6 py-3 rounded-lg font-medium border-2 flex items-center justify-center gap-2 transition-all duration-300 hover:bg-slate-100"
            style={{ borderColor: primaryColor, color: primaryColor }}
            onClick={handleGoBack}
          >
            <ArrowLeft size={18} />
            <span>Go Back</span>
          </button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes ripple {
          0% { transform: scale(0.5); opacity: 0.2; }
          50% { opacity: 0.3; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        
        @keyframes widthPulse {
          0% { width: 24px; }
          100% { width: 120px; }
        }
        
        @keyframes float {
          0% { transform: translateY(0) rotate(0); opacity: 0; }
          10% { opacity: 0.2; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        
        @keyframes growWidth {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}